import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BadgeCheck } from 'lucide-react';

function BrandLogo({ cs, size = 'md' }) {
  const dim = size === 'lg' ? 'w-14 h-14 text-lg' : 'w-11 h-11 text-sm';
  const [failed, setFailed] = React.useState(!cs.logo);
  const initials = cs.brand.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  if (!failed) {
    return (
      <span className={`${dim} rounded-xl bg-white grid place-items-center p-1.5 shrink-0`}>
        <img src={cs.logo} alt={cs.brand} className="max-w-full max-h-full object-contain" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <span className={`${dim} rounded-xl bg-gradient-to-br from-[#2563eb]/15 to-[#1d4ed8]/15 border border-blue-200 grid place-items-center font-display font-bold text-blue-700 shrink-0`}>
      {initials}
    </span>
  );
}
export { BrandLogo };

export default function CaseStudyCard({ cs }) {
  const rows = [
    ['Industry', cs.industry],
    ['Market', cs.market],
    ['Campaign', cs.campaignType],
    ['Installs', cs.installs],
    ['ROAS', cs.roas],
    ['CVR', cs.conversionRate],
  ].filter(([, v]) => v && v !== '\u2014');

  return (
    <Link to={`/case-studies/${cs.slug}`} className="reveal group block rounded-2xl overflow-hidden glass glass-hover">
      <div className="relative h-40 overflow-hidden">
        <img src={cs.image} alt={cs.brand} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b12] via-[#0a0b12]/40 to-transparent" />
        <span className="absolute top-3 left-3 rounded-full bg-black/55 backdrop-blur px-3 py-1 text-[11px] font-medium text-white border border-white/25">{cs.category}</span>
        {cs.verified && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-emerald-500/30 backdrop-blur px-2.5 py-1 text-[10px] font-semibold text-emerald-200 border border-emerald-300/50">
            <BadgeCheck size={12} /> {cs.verified}
          </span>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-center gap-3">
          <BrandLogo cs={cs} />
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold text-slate-900 leading-tight truncate">{cs.brand}</h3>
            <p className="text-xs text-slate-500 truncate">{cs.objective}</p>
          </div>
          <ArrowUpRight size={20} className="text-slate-500 group-hover:text-[#2563eb] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
        </div>

        {/* headline metrics */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          {cs.metrics.map((m) => (
            <div key={m.label} className="text-center rounded-lg bg-slate-50 border border-slate-200 py-2">
              <div className="font-display text-base font-bold text-gradient-cyan">{m.value}</div>
              <div className="text-[9px] text-slate-500 leading-tight mt-0.5">{m.label}</div>
            </div>
          ))}
        </div>

        {/* structured detail rows */}
        <dl className="mt-4 space-y-1.5">
          {rows.slice(0, 4).map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-3 text-xs">
              <dt className="text-slate-500 shrink-0">{k}</dt>
              <dd className="text-slate-600 text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Link>
  );
}
